import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {paths} from "@/lib/paths";
import {
  addCartVoucher,
  ApiError,
  cancelGuestCheckout,
  enableGuestCheckout,
  getCheckoutAddress,
  getCheckoutCompany,
  getCheckoutContext,
  placeOrder,
  productImageFromUrl,
  removeCartVoucher,
  stripVoucherQueryParams,
  voucherCodeFromSearchParams,
  type CheckoutContext,
  type FieldRule,
  type I18nKey
} from "@zaaaz/shared";
import {DsButton, DsField, DsInput} from "@zaaaz/ui";
import {useFormValidation} from "@/lib/useFormValidation";
import {useI18n} from "./I18nProvider";
import {MoneyDisplay} from "./MoneyDisplay";
import {ProductPrice} from "./ProductPrice";

type CheckoutField = "nume" | "telefon" | "email" | "adresa" | "judet" | "localitate" | "firma_nume" | "firma_cui" | "firma_j" | "firma_banca" | "firma_cont" | "firma_sediu";

function checkoutRules(tip: number): FieldRule<CheckoutField>[] {
  const rules: FieldRule<CheckoutField>[] = [
    {field: "nume", required: true},
    {field: "telefon", required: true},
    {field: "email", type: "email", required: true},
    {field: "adresa", required: true},
    {field: "judet", type: "select", required: true},
    {field: "localitate", required: true}
  ];
  if(tip === 1) {
    rules.push(
      {field: "firma_nume", required: true},
      {field: "firma_cui", required: true},
      {field: "firma_j", required: true},
      {field: "firma_sediu", required: true},
      {field: "firma_banca", required: true},
      {field: "firma_cont", required: true}
    );
  }
  return rules;
}

function SectionHead({icon, title}: {icon: string; title: string}) {
  return (
    <div className="cc-checkout__sectionHead">
      <span className="material-symbols-rounded cc-checkout__sectionIcon" aria-hidden="true">{icon}</span>
      <h2 className="cc-checkout__sectionTitle">{title}</h2>
    </div>
  );
}

function CheckoutSidebar({
  ctx,
  t,
  terms,
  setTerms,
  error,
  loading,
  voucherCode,
  setVoucherCode,
  voucherErr,
  voucherMsg,
  onVoucher,
  onRemoveVoucher,
  formReady
}: {
  ctx: CheckoutContext;
  t: (k: I18nKey) => string;
  terms: boolean;
  setTerms: (v: boolean) => void;
  error: string;
  loading: boolean;
  voucherCode: string;
  setVoucherCode: (v: string) => void;
  voucherErr: boolean;
  voucherMsg: string;
  onVoucher: (e: React.FormEvent) => void;
  onRemoveVoucher: (id: number) => void;
  formReady: boolean;
}) {
  const c = ctx.cart;
  const rate = c.eurRate || 4.97;
  const hasDiscount = c.discount > 0;
  const productsTotal = hasDiscount ? c.totalAfterDiscount : c.subtotal;
  const shippingLabel = c.freeDelivery ? t("Free") : (c.shippingCost === null ? "-" : <MoneyDisplay lei={c.shippingCost} eurRate={rate} />);
  const total = c.shippingCost === null ? productsTotal : c.grandTotal;

  return (
    <aside className="cc-checkout__aside">
      <div className="cc-checkout__summaryCard">
        <h2 className="cc-checkout__summaryTitle">{t("Order summary")}</h2>
        <ul className="cc-checkout__summaryItems">
          {c.lines.map((line) => (
            <li key={`${line.warehouse}-${line.productId}`} className="cc-checkout__summaryItem">
              <Link to={line.product.url} className="cc-checkout__summaryThumb anim">
                <img src={productImageFromUrl(line.product.imagine)} alt="" />
              </Link>
              <div className="cc-checkout__summaryMeta">
                <Link to={line.product.url} className="cc-checkout__summaryName anim">{line.product.nume}</Link>
                <span className="cc-checkout__summaryQty">{t("Quantity label")} {line.quantity}</span>
              </div>
              <div className="cc-checkout__summaryPrice">
                <ProductPrice pretEuro={line.pretEuro || line.product.pretEuro} pretLei={line.pretLei} />
              </div>
            </li>
          ))}
        </ul>
        <div className="cc-checkout__summaryRows">
          <div className="cc-checkout__summaryRow">
            <span>{t("Products")}</span>
            <b><MoneyDisplay lei={productsTotal} eurRate={rate} /></b>
          </div>
          {hasDiscount ? (
            <div className="cc-checkout__summaryRow">
              <span>{t("Discount")}</span>
              <b>-<MoneyDisplay lei={c.discount} eurRate={rate} /></b>
            </div>
          ) : null}
          <div className="cc-checkout__summaryRow">
            <span>{t("Shipping")}</span>
            <b>{shippingLabel}</b>
          </div>
          <div className="cc-checkout__summaryRow cc-checkout__summaryRow--total">
            <span>{t("Total")}</span>
            <b className="cc-checkout__summaryTotal"><MoneyDisplay lei={total} eurRate={rate} /></b>
          </div>
        </div>
        <div className="cc-checkout__payBadge" aria-hidden="true">
          <span className="cc-checkout__payBadgeLabel">{t("Payment method")}</span>
          <img src="/images/mobilpay.jpg" alt="" className="cc-checkout__payBadgeImg" />
        </div>
        {c.vouchers.length ? (
          <ul className="cc-checkout__voucherList">
            {c.vouchers.map((v) => (
              <li key={v.id}>
                <span>{v.label}</span>
                <button type="button" className="cc-checkout__voucherRemove anim" onClick={() => onRemoveVoucher(v.id)} aria-label={t("Remove")}>×</button>
              </li>
            ))}
          </ul>
        ) : null}
        <form className="cc-checkout__coupon" onSubmit={onVoucher}>
          <DsInput
            type="text"
            name="voucher"
            value={voucherCode}
            onChange={(e) => setVoucherCode(e.target.value)}
            placeholder={t("Coupon code")}
            className="cc-checkout__couponInput"
            error={voucherErr ? (voucherMsg || t("Invalid voucher code")) : undefined}
          />
          <button type="submit" className="cc-checkout__couponBtn anim">{t("Apply price")}</button>
        </form>
        {!voucherErr && voucherMsg ? (
          <p className="cc-checkout__alert cc-checkout__alert--ok">{voucherMsg}</p>
        ) : null}
        <label className="cc-checkout__terms">
          <input type="checkbox" name="terms" value="1" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>
            {t("I agree with")}{" "}
            <a href={paths.terms} className="anim" target="_blank" rel="noopener noreferrer">{t("the terms and conditions")}</a>
          </span>
        </label>
        {error ? <p className="cc-checkout__alert cc-checkout__alert--err">{error}</p> : null}
        {loading ? <div className="cc-checkout__processing">{t("Order is processing...")}</div> : null}
        <DsButton type="submit" variant="primary" size="md" fullWidth className="cc-checkout__placeBtn" disabled={loading}>
          {t("Place order")}
        </DsButton>
        {!formReady || !terms ? (
          <p className="cc-checkout__hint">{t("Check address to continue")}</p>
        ) : null}
        <div className="cc-checkout__trust">
          <div className="cc-checkout__trustItem">
            <span className="material-symbols-rounded" aria-hidden="true">lock</span>
            <span>{t("Checkout trust secure")}</span>
          </div>
          <div className="cc-checkout__trustItem">
            <span className="material-symbols-rounded" aria-hidden="true">published_with_changes</span>
            <span>{t("Checkout trust returns")}</span>
          </div>
          <div className="cc-checkout__trustItem">
            <span className="material-symbols-rounded" aria-hidden="true">flag</span>
            <span>{t("Checkout trust local")}</span>
          </div>
        </div>
        <Link to={paths.cart} className="cc-checkout__backCart anim">{t("Back to shopping")}</Link>
      </div>
    </aside>
  );
}

export function CheckoutView() {
  const {t} = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoVoucherDone = useRef("");
  const [ctx, setCtx] = useState<CheckoutContext | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tip, setTip] = useState(0);
  const [selectedCompanyId, setSelectedCompanyId] = useState(0);
  const [metodaPlata, setMetodaPlata] = useState<number>(0);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherErr, setVoucherErr] = useState(false);
  const [voucherMsg, setVoucherMsg] = useState("");
  const {errors, clearError, validate} = useFormValidation<CheckoutField>();
  const [terms, setTerms] = useState(false);
  const [form, setForm] = useState({
    nume: "", telefon: "", email: "", adresa: "", judet: 0, localitate: "", codpostal: "",
    firma_nume: "", firma_cui: "", firma_j: "", firma_banca: "", firma_cont: "", firma_sediu: "", mesaj: ""
  });

  const loadContext = useCallback(async (judet?: number) => {
    const r = await getCheckoutContext(judet && judet > 0 ? judet : undefined, 50);
    setCtx(r);
    return r;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fromUrl = voucherCodeFromSearchParams(searchParams);
      if(fromUrl && autoVoucherDone.current !== fromUrl) {
        autoVoucherDone.current = fromUrl;
        try {
          await addCartVoucher(fromUrl);
          if(cancelled) return;
          setVoucherErr(false);
          setVoucherMsg(t("Voucher applied successfully"));
          setVoucherCode("");
        } catch(e) {
          if(cancelled) return;
          setVoucherCode(fromUrl);
          setVoucherErr(true);
          setVoucherMsg(
            e instanceof ApiError && e.body?.code === "SELF_VOUCHER"
              ? t("You cannot use your own influencer voucher")
              : t("Invalid voucher code")
          );
        }
        setSearchParams(stripVoucherQueryParams(searchParams), {replace: true});
      }
      try {
        const r = await loadContext();
        if(cancelled) return;
        setForm((f) => ({
          ...f,
          nume: r.client.nume,
          telefon: r.client.telefon,
          email: r.client.email,
          firma_nume: r.client.firma_nume,
          firma_cui: r.client.firma_cui,
          firma_j: r.client.firma_j,
          firma_banca: r.client.firma_banca,
          firma_cont: r.client.firma_cont,
          firma_sediu: r.client.firma_sediu
        }));
      } catch(e) {
        if(!cancelled) setError(e instanceof Error ? e.message : t("Cart update failed"));
      }
    })();
    return () => { cancelled = true; };
  }, [loadContext]);

  const formReady = useMemo(() => {
    const baseOk = Boolean(form.nume.trim() && form.telefon.trim() && form.email.trim() && form.adresa.trim() && form.judet > 0 && form.localitate.trim());
    if(!baseOk) return false;
    if(tip === 0) return true;
    return Boolean(form.firma_nume.trim() && form.firma_cui.trim() && form.firma_j.trim() && form.firma_sediu.trim() && form.firma_banca.trim() && form.firma_cont.trim());
  }, [form, tip]);

  function setField(k: keyof typeof form, v: string | number) {
    setForm((f) => ({...f, [k]: v}));
    clearError(k as CheckoutField);
  }

  async function onJudetChange(judet: number) {
    setField("judet", judet);
    try {
      await loadContext(judet);
    } catch(e) {
      setError(e instanceof Error ? e.message : t("Cart update failed"));
    }
  }

  async function loadAddress(id: number) {
    if(id === 0) {
      setForm((f) => ({...f, adresa: "", judet: 0, localitate: "", codpostal: ""}));
      await loadContext();
      return;
    }
    const a = await getCheckoutAddress(id);
    setForm((f) => ({...f, adresa: a.adresa, judet: a.judet, localitate: a.localitate, codpostal: String(a.codpostal || "")}));
    await loadContext(a.judet);
  }

  async function loadCompany(id: number) {
    setSelectedCompanyId(id);
    if(id === 0) {
      setForm((f) => ({...f, firma_nume: "", firma_cui: "", firma_j: "", firma_banca: "", firma_cont: "", firma_sediu: ""}));
      return;
    }
    const c = await getCheckoutCompany(id);
    setForm((f) => ({...f, firma_nume: c.firma_nume, firma_cui: c.firma_cui, firma_j: c.firma_j, firma_banca: c.firma_banca, firma_cont: c.firma_cont, firma_sediu: c.firma_sediu}));
  }

  async function onGuest(enable: boolean) {
    try {
      if(enable) await enableGuestCheckout();
      else await cancelGuestCheckout();
      await loadContext(form.judet > 0 ? form.judet : undefined);
    } catch(e) {
      setError(e instanceof Error ? e.message : t("Cart update failed"));
    }
  }

  async function onVoucher(e: React.FormEvent) {
    e.preventDefault();
    if(!voucherCode.trim()) return;
    setVoucherErr(false);
    setVoucherMsg("");
    try {
      await addCartVoucher(voucherCode.trim());
      setVoucherCode("");
      setVoucherMsg(t("Voucher applied successfully"));
      await loadContext(form.judet > 0 ? form.judet : undefined);
    } catch(err) {
      setVoucherErr(true);
      setVoucherMsg(
        err instanceof ApiError && err.body?.code === "SELF_VOUCHER"
          ? t("You cannot use your own influencer voucher")
          : t("Invalid voucher code")
      );
    }
  }

  async function onRemoveVoucher(id: number) {
    try {
      await removeCartVoucher(id);
      setVoucherErr(false);
      setVoucherMsg("");
      await loadContext(form.judet > 0 ? form.judet : undefined);
    } catch(e) {
      setError(e instanceof Error ? e.message : t("Cart update failed"));
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if(!validate(checkoutRules(tip), form, t)) return;
    if(!terms) { setError(t("Please accept the terms and conditions")); return; }
    setLoading(true);
    try {
      const r = await placeOrder({
        ...form,
        tip,
        metodaLivrare: ctx?.deliveryMethod ?? 50,
        metodaPlata,
        judet: form.judet,
        codpostal: form.codpostal
      });
      setSuccess(true);
      window.dispatchEvent(new CustomEvent("cart-updated", {detail: 0}));
      navigate(r.redirectUrl);
    } catch(err) {
      setError(err instanceof Error ? err.message : t("Order failed"));
    } finally {
      setLoading(false);
    }
  }

  if(error && !ctx) {
    return <div className="cc-checkout"><p className="cc-checkout__alert cc-checkout__alert--err">{error}</p></div>;
  }
  if(!ctx) {
    return (
      <div className="cc-checkout">
        <div className="cc-checkout__loading">
          <div className="cc-checkout__spinner" aria-hidden="true" />
          <span>{t("Loading products...")}</span>
        </div>
      </div>
    );
  }

  const rate = ctx.cart.eurRate || 4.97;
  const shipPrice = ctx.cart.freeDelivery ? t("Free") : (ctx.cart.shippingCost === null ? "-" : <MoneyDisplay lei={ctx.cart.shippingCost} eurRate={rate} />);

  return (
    <div className="cc-checkout">
      <div className="cc-checkout__shell inside">
        <header className="cc-checkout__pageHead">
          <Link to={paths.cart} className="cc-checkout__back anim">
            <i className="fa fa-long-arrow-left" aria-hidden="true" /> {t("Back")}
          </Link>
          <h1 className="cc-checkout__pageTitle">{t("Checkout")}</h1>
        </header>

        {success ? (
          <div className="cc-checkout__success" id="raspunsPlasareComanda">
            <div className="cc-checkout__successRing"><i className="fa fa-check" aria-hidden="true" /></div>
            <h2 className="cc-checkout__successTitle">{t("Your order has been successfully registered!")}</h2>
            <p className="cc-checkout__successText">
              {ctx.isGuest ? (
                t("Guest order confirmation hint")
              ) : (
                <>
                  {t("You can check the status of the order by accessing")}{" "}
                  <Link to={paths.account} className="anim">{t("your account")}</Link>{" "}
                  {t("or following your e-mail address.")}
                </>
              )}
            </p>
          </div>
        ) : (
          <form className="cc-checkout__layout" id="orderForm" onSubmit={onSubmit} noValidate>
            <div className="cc-checkout__main">
              <section className="cc-checkout__section">
                <SectionHead icon="home" title={t("Shipping address")} />
                <div className="cc-checkout__fields">
                  <DsInput type="email" name="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder={t("E-mail address")} className="cc-checkout__input" error={errors.email} aria-required="true" />
                  <DsInput type="text" name="nume" value={form.nume} onChange={(e) => setField("nume", e.target.value)} placeholder={t("Name and surname")} className="cc-checkout__input" error={errors.nume} aria-required="true" />
                  <DsInput type="text" name="telefon" value={form.telefon} onChange={(e) => setField("telefon", e.target.value)} placeholder={t("Phone number")} className="cc-checkout__input" error={errors.telefon} aria-required="true" />
                  <div className="cc-checkout__tabs" role="radiogroup" aria-label={t("Billing information")}>
                    <button type="button" className={`cc-checkout__tab anim ${tip === 0 ? "cc-checkout__tab--active" : ""}`} onClick={() => { setTip(0); setSelectedCompanyId(0); }}>{t("Individual")}</button>
                    <button type="button" className={`cc-checkout__tab anim ${tip === 1 ? "cc-checkout__tab--active" : ""}`} onClick={() => setTip(1)}>{t("Company")}</button>
                  </div>
                  {tip === 1 ? (
                    <div className="cc-checkout__company">
                      {ctx.companies.length ? (
                        <div className="cc-checkout__addrBar">
                          <span className="cc-checkout__addrLabel">{t("Saved companies")}</span>
                          <div className="cc-checkout__addrPills">
                            <button type="button" className={`cc-checkout__pill anim ${selectedCompanyId === 0 ? "cc-checkout__pill--active" : ""}`} onClick={() => loadCompany(0)}>{t("New company")}</button>
                            {ctx.companies.map((c) => (
                              <button type="button" key={c.id} className={`cc-checkout__pill anim ${selectedCompanyId === c.id ? "cc-checkout__pill--active" : ""}`} onClick={() => loadCompany(c.id)}>{c.firma_nume}</button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <div className="cc-checkout__row2">
                        <DsInput type="text" name="firma_nume" value={form.firma_nume} onChange={(e) => setField("firma_nume", e.target.value)} placeholder={t("Company name")} className="cc-checkout__input" error={errors.firma_nume} aria-required="true" />
                        <DsInput type="text" name="firma_cui" value={form.firma_cui} onChange={(e) => setField("firma_cui", e.target.value)} placeholder={t("VAT Number")} className="cc-checkout__input" error={errors.firma_cui} aria-required="true" />
                      </div>
                      <DsInput type="text" name="firma_j" value={form.firma_j} onChange={(e) => setField("firma_j", e.target.value)} placeholder={t("Trade register no.")} className="cc-checkout__input" error={errors.firma_j} aria-required="true" />
                      <DsInput type="text" name="firma_sediu" value={form.firma_sediu} onChange={(e) => setField("firma_sediu", e.target.value)} placeholder={t("Office address")} className="cc-checkout__input" error={errors.firma_sediu} aria-required="true" />
                      <div className="cc-checkout__row2">
                        <DsInput type="text" name="firma_banca" value={form.firma_banca} onChange={(e) => setField("firma_banca", e.target.value)} placeholder={t("Bank")} className="cc-checkout__input" error={errors.firma_banca} aria-required="true" />
                        <DsInput type="text" name="firma_cont" value={form.firma_cont} onChange={(e) => setField("firma_cont", e.target.value)} placeholder={t("Bank account")} className="cc-checkout__input" error={errors.firma_cont} aria-required="true" />
                      </div>
                    </div>
                  ) : null}
                  {ctx.addresses.length ? (
                    <div className="cc-checkout__addrBar">
                      {!ctx.guestMode ? (
                        <>
                          <span className="cc-checkout__addrLabel">{t("Saved addresses")}</span>
                          <div className="cc-checkout__addrPills">
                            <button type="button" className="cc-checkout__pill cc-checkout__pill--active anim" onClick={() => loadAddress(0)}>{t("New address")}</button>
                            {ctx.addresses.map((a) => (
                              <button type="button" key={a.id} className="cc-checkout__pill anim" onClick={() => loadAddress(a.id)}>{a.nume}</button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <button type="button" className="cc-checkout__pill anim" onClick={() => onGuest(false)}>{t("Use saved addresses")}</button>
                      )}
                    </div>
                  ) : null}
                  <DsInput type="text" name="adresa" value={form.adresa} onChange={(e) => setField("adresa", e.target.value)} placeholder={t("Address (street, number)")} className="cc-checkout__input" error={errors.adresa} aria-required="true" />
                  <div className="cc-checkout__row3">
                    <DsInput type="text" name="codpostal" value={form.codpostal} onChange={(e) => setField("codpostal", e.target.value)} placeholder={t("Zip code")} className="cc-checkout__input" />
                    <DsInput type="text" name="localitate" value={form.localitate} onChange={(e) => setField("localitate", e.target.value)} placeholder={t("City")} className="cc-checkout__input" error={errors.localitate} aria-required="true" />
                    <DsField error={errors.judet}>
                      <select name="judet" value={form.judet} onChange={(e) => onJudetChange(parseInt(e.target.value, 10))} className="cc-checkout__input cc-checkout__select cc-ds-input" aria-required="true">
                        <option value={0}>{t("County")}</option>
                        {ctx.counties.map((j) => <option key={j.id} value={j.id}>{j.nume}</option>)}
                      </select>
                    </DsField>
                  </div>
                </div>
              </section>

              <section className="cc-checkout__section">
                <SectionHead icon="local_shipping" title={t("Shipping methods")} />
                <label className="cc-checkout__shipOption cc-checkout__shipOption--active">
                  <input type="radio" name="metodaLivrare" value={ctx.deliveryMethod} checked readOnly className="cc-checkout__shipRadio" />
                  <span className="cc-checkout__shipOptionBody">
                    <span className="cc-checkout__shipOptionMain">
                      <strong>{t("Home delivery")}</strong>
                      <span className="cc-checkout__shipOptionSub">{t("SameDay")} · {t("Delivery in Romania")}</span>
                    </span>
                    <span className="cc-checkout__shipOptionPrice">{shipPrice}</span>
                  </span>
                </label>
              </section>

              <section className="cc-checkout__section">
                <SectionHead icon="credit_card" title={t("Payment method")} />
                <div className="cc-checkout__payList">
                  {ctx.paymentMethods.map((pm) => {
                    const active = metodaPlata === pm.id;
                    return (
                      <div key={pm.id} className={`cc-checkout__payOption${active ? " cc-checkout__payOption--active" : ""}`}>
                        <button type="button" className="cc-checkout__payOptionHead anim" onClick={() => setMetodaPlata(pm.id)}>
                          <span className="cc-checkout__payRadio" aria-hidden="true" />
                          <span className="cc-checkout__payOptionLabel">{pm.id === 0 ? t("Cash on delivery") : t("Credit / Debit card")}</span>
                          {pm.id === 1 ? <img src="/images/mobilpay.jpg" alt="" className="cc-checkout__payOptionLogos" /> : null}
                        </button>
                        {active && pm.id === 1 ? (
                          <div className="cc-checkout__payOptionBody">
                            <p>{t("Card payment secure hint")}</p>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="cc-checkout__section cc-checkout__section--message">
                <SectionHead icon="chat" title={t("Leave a message")} />
                <DsField>
                  <textarea name="mesaj" value={form.mesaj} onChange={(e) => setField("mesaj", e.target.value)} placeholder={t("Leave a message")} className="cc-checkout__textarea cc-ds-input" rows={3} />
                </DsField>
              </section>
            </div>

            <CheckoutSidebar
              ctx={ctx}
              t={t}
              terms={terms}
              setTerms={setTerms}
              error={error}
              loading={loading}
              voucherCode={voucherCode}
              setVoucherCode={setVoucherCode}
              voucherErr={voucherErr}
              voucherMsg={voucherMsg}
              onVoucher={onVoucher}
              onRemoveVoucher={onRemoveVoucher}
              formReady={formReady}
            />
          </form>
        )}
      </div>
    </div>
  );
}
